import axios from 'axios';
import auth from '@/services/auth';
import { showGlobalApiError } from '@/utils/apiError';

const api = axios.create({
  baseURL: process.env.VUE_APP_API_URL || 'http://localhost:8000/api'
});

// This interceptor adds the JWT token to every request and sets appropriate Content-Type
api.interceptors.request.use(config => {
  // Public endpoints that must work without auth (and without 401→login bounce)
  const isPublicUnauth =
    config.url &&
    (config.url.includes('/secure-upload/') ||
      config.url.includes('/users/agreements/sign/'));

  const token = auth.getToken();
  if (token && !isPublicUnauth) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Only set Content-Type to JSON if it's not already set and it's not FormData
  if (!config.headers['Content-Type'] && !(config.data instanceof FormData)) {
    config.headers['Content-Type'] = 'application/json';
  }
  
  return config;
}, error => {
  return Promise.reject(error);
});

// This interceptor handles token refreshing when a token expires
api.interceptors.response.use(response => {
  return response;
}, async error => {
  const originalRequest = error.config;
  const excludedUrls = ['/token/', '/token/refresh/', '/secure-upload/'];

  // Check for 401 Unauthorized, not a retry, and not an excluded URL
  // Also exclude secure upload / public agreement signing URLs
  const isPublicUnauth =
    originalRequest.url.includes('/secure-upload/') ||
    originalRequest.url.includes('/users/agreements/sign/');
  if (error.response && error.response.status === 401 && !originalRequest._retry && !excludedUrls.includes(originalRequest.url) && !isPublicUnauth) {
    originalRequest._retry = true;
    try {
      console.log('Attempting to refresh token...');
      await auth.refreshToken();
      console.log('Token refreshed, retrying original request.');
      return api(originalRequest);
    } catch (refreshError) {
      console.error('Unable to refresh token.', refreshError);
      auth.logout();
      window.location.href = '/login';
      return Promise.reject(refreshError);
    }
  }

  const status = error.response?.status;
  if (
    status
    && [402, 403, 429].includes(status)
    && !originalRequest._skipGlobalErrorToast
  ) {
    showGlobalApiError(error);
  }

  return Promise.reject(error);
});

export default api;